import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  VStack,
  HStack,
  Text,
  Textarea,
  Switch,
  FormHelperText,
  Grid,
} from '@chakra-ui/react'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { applicationSchema, type ApplicationFormData } from '@/lib/validations/application'

interface ApplicationFormProps {
  applicationId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

export function ApplicationForm({ applicationId, onSuccess, onCancel }: ApplicationFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const { showError, showSuccess } = useToastNotification()
  const isEditMode = !!applicationId

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<ApplicationFormData>({
    resolver: zodResolver(applicationSchema),
    defaultValues: {
      name: '',
      code: '',
      description: '',
      active: true,
    },
  })

  const activeValue = watch('active')

  useEffect(() => {
    if (isEditMode && applicationId) {
      const load = async () => {
        setIsLoading(true)
        try {
          const res = await apiClient.get(`/system-settings/applications/${applicationId}`)
          const d = res.data?.data ?? res.data ?? {}
          reset({
            name: d.name ?? '',
            code: d.code ?? '',
            description: d.description ?? '',
            active: d.active === true || d.active_label === 'Active',
          })
        } catch (e) {
          showError(e)
        } finally {
          setIsLoading(false)
        }
      }
      load()
    }
  }, [applicationId, isEditMode, reset, showError])

  const onSubmit = async (data: ApplicationFormData) => {
    setIsSubmitting(true)
    try {
      const payload = {
        name: data.name,
        code: data.code,
        description: data.description ?? '',
        active: data.active,
      }
      if (isEditMode && applicationId) {
        await apiClient.put(`/system-settings/applications/${applicationId}`, payload)
        showSuccess('Success', 'Application updated successfully')
      } else {
        await apiClient.post('/system-settings/applications', payload)
        showSuccess('Success', 'Application created successfully')
      }
      onSuccess?.()
    } catch (e) {
      showError(e)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <Box p={4}>
        <Text>Loading application...</Text>
      </Box>
    )
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={4} align="stretch">
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Name *</FormLabel>
          <FormControl isInvalid={!!errors.name}>
            <Input {...register('name')} placeholder="e.g. Auth Web App" />
            <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
          </FormControl>
        </Grid>
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Code *</FormLabel>
          <FormControl isInvalid={!!errors.code}>
            <Input {...register('code')} placeholder="e.g. auth" isDisabled={isEditMode} />
            {isEditMode && <FormHelperText>Code cannot be changed after creation.</FormHelperText>}
            <FormErrorMessage>{errors.code?.message}</FormErrorMessage>
          </FormControl>
        </Grid>
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Description</FormLabel>
          <FormControl>
            <Textarea {...register('description')} placeholder="Optional" rows={3} />
          </FormControl>
        </Grid>
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Active</FormLabel>
          <FormControl>
            <Switch isChecked={activeValue} onChange={(e) => setValue('active', e.target.checked)} />
            <FormHelperText>When inactive, hidden from selection.</FormHelperText>
          </FormControl>
        </Grid>
        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel} isDisabled={isSubmitting}>Cancel</Button>
          )}
          <Button type="submit" colorScheme="blue" isLoading={isSubmitting} loadingText={isEditMode ? 'Saving...' : 'Creating...'}>
            {isEditMode ? 'Save' : 'Create Application'}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}
